import { Platform } from 'react-native';

// On web, the browser is on the same machine as the backend → use localhost.
// On native (iOS/Android device or emulator), use the machine's LAN IP.
const DEV_NATIVE_IP = '10.50.78.78';

const DEFAULT_API_URLS = {
  development: Platform.OS === 'web'
    ? 'http://localhost:8082'
    : `http://${DEV_NATIVE_IP}:8082`,
  staging: 'https://lnb-backend-staging-geusdhf7kq-el.a.run.app',
  // Production Cloud Run (`lnb-backend-production-...`) is not deployed and
  // returns HTML 404s. Use staging until a real production service exists.
  production: 'https://lnb-backend-staging-geusdhf7kq-el.a.run.app',
} as const;

type EnvironmentKey = keyof typeof DEFAULT_API_URLS;

const sanitizeBaseUrl = (url: string) => url.replace(/\/+$/, '');

// Avoid optional chaining on process.env.* — webpack/babel env inlining turns
// missing vars into `undefined`, which can compile `env?.trim()` into `(void 0)()`.
const rawApiUrl = String(process.env.EXPO_PUBLIC_API_URL || '').trim() || undefined;
const rawFirebaseEnv = String(process.env.EXPO_PUBLIC_FIREBASE_ENV || '').trim().toLowerCase();
// Public reCAPTCHA Enterprise site key for LNB-Web App Check (not a secret).
// Env is preferred; fallback keeps web builds working if the bundler drops the var.
const DEFAULT_RECAPTCHA_ENTERPRISE_SITE_KEY = '6LchQaEtAAAAAILKrXis6PNcp9S1tJVl1EAyhxRX';
const rawRecaptchaEnterpriseSiteKey = String(
  process.env.EXPO_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY || DEFAULT_RECAPTCHA_ENTERPRISE_SITE_KEY
).trim();
const rawAppCheckDebugToken = String(process.env.EXPO_PUBLIC_APPCHECK_DEBUG_TOKEN || '').trim();
const envName = (rawFirebaseEnv as EnvironmentKey) || (__DEV__ ? 'development' : 'staging');
const fallbackApiUrl = DEFAULT_API_URLS[envName] || DEFAULT_API_URLS.development;

// Only log environment variables in development to prevent exposure in production
if (__DEV__) {
  console.log('🔧 ENV - process.env.EXPO_PUBLIC_API_URL:', process.env.EXPO_PUBLIC_API_URL);
  console.log('🔧 ENV - rawApiUrl:', rawApiUrl);
  console.log('🔧 ENV - __DEV__:', __DEV__);
  console.log('🔧 ENV - EXPO_PUBLIC_FIREBASE_ENV:', process.env.EXPO_PUBLIC_FIREBASE_ENV);
  console.log(
    '🔧 ENV - EXPO_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY:',
    rawRecaptchaEnterpriseSiteKey ? '(set)' : '(missing)'
  );
}

/**
 * Shared API base URL for all network calls. Updates without a new build by
 * reading the EXPO_PUBLIC_API_URL env var (falls back to a sane default for dev).
 */
export const apiBaseUrl = sanitizeBaseUrl(rawApiUrl || fallbackApiUrl);

/** reCAPTCHA Enterprise site key used by web Firebase App Check. Never log the raw value. */
export const recaptchaEnterpriseSiteKey = rawRecaptchaEnterpriseSiteKey || undefined;

/** Optional App Check debug token (`true` to generate, or a console-registered UUID). */
export const appCheckDebugToken = rawAppCheckDebugToken || undefined;

console.log('🔧 ENV - Final apiBaseUrl:', apiBaseUrl);

export const buildApiUrl = (path = '') => {
  const normalizedPath = path.replace(/^\/+/, '');
  if (!normalizedPath) {
    return apiBaseUrl;
  }
  return `${apiBaseUrl}/${normalizedPath}`;
};

if (__DEV__ && !rawApiUrl) {
  console.warn(`[env] EXPO_PUBLIC_API_URL is not set. Falling back to ${fallbackApiUrl}.`);
}
